import React from 'react';
import { observer, inject } from 'mobx-react';
import { observable, action } from 'mobx';
import { withNamespaces } from 'react-i18next';

import PropTypes from 'prop-types';

import { staticURL } from 'core/url';
import { Paragraph } from 'components';
import Header from 'apps/components/header/header';

import {
  AccordionStyled,
  Version,
  ButtonStyled,
  Image,
  Wrapper,
} from './styles';

class AllExpandedStore {
  @observable allExpanded = false;
  @action SetAllExpanded = bool => {
    this.allExpanded = bool;
  };
}

@withNamespaces()
@inject('routerStore')
@observer
export class FAQPage extends React.Component {
  exStore = new AllExpandedStore();
  render() {
    const { allExpanded, SetAllExpanded } = this.exStore;

    return (
      <div>
        <Header />
        <Wrapper>
          <Version>
            v1.0, <i>last updated 12/11/2018</i>
          </Version>
          <ButtonStyled
            onClick={() => SetAllExpanded(!allExpanded)}
            variant={allExpanded ? 'destructive' : 'primary'}
            size='small'
            text={allExpanded ? 'Reset' : 'Expand all'}
          />
          <AccordionStyled title='What is Wikifactory?' unfolded={allExpanded}>
            <Paragraph>
              Wikifactory is a &quot;social design and production&quot;
              platform, in a similar vein to how for example youtube is a social
              video platform, or github a social coding platform.
            </Paragraph>
            <Paragraph>You can think of it as a combination of:</Paragraph>
            <Paragraph>
              1) an online community of designers, engineers, makers,
              technologists and other creative problem solvers, and
            </Paragraph>
            <Paragraph>
              2) a set of collaboration tools designed to help you and your team
              work on physical products of all stages and sizes.
            </Paragraph>
          </AccordionStyled>
          <AccordionStyled
            title='What features does this alpha have?'
            unfolded={allExpanded}
          >
            <Paragraph>
              Right now on Wikifactory, you can create public or private
              projects and:
            </Paragraph>
            <ul>
              <li>
                <strong>
                  securely host files in a version controlled cloud system
                </strong>
                <Image src={staticURL('/assets/faq/version-control.png')} />
              </li>
              <li>
                <strong>
                  visualise over 30 professional CAD and CAE formats without the
                  need for plugins or expensive licenses
                </strong>
                <Image src={staticURL('/assets/faq/rendering.png')} />
              </li>
              <li>
                <strong>
                  help others understand, replicate and contribute to your
                  projects through a flexible documentation tool
                </strong>
                <Image src={staticURL('/assets/faq/documentation.png')} />
              </li>
              <li>
                <strong>
                  track enhancements, tasks, or bugs. Organise them via labels
                  and assign them to collaborators
                </strong>
                <Image src={staticURL('/assets/faq/issue-tracking.png')} />
              </li>
              <li>
                <strong>
                  review other&apos;s work and share annotated files with
                  manufacturers
                </strong>
                <Image src={staticURL('/assets/faq/plm-visualisation.png')} />
              </li>
              <li>
                <strong>
                  assign different roles and permissions as your team of
                  collaborators grows
                </strong>
                <Image src={staticURL('/assets/faq/permissions.png')} />
              </li>
            </ul>
            <Paragraph>
              In addition, some of our social features include:
            </Paragraph>
            <ul>
              <li>
                <strong>
                  profile pages to introduce yourself and beautifully showcase
                  your work
                </strong>
                <Image src={staticURL('/assets/faq/user-profiles.png')} />
              </li>
              <li>
                <strong>
                  organisation pages for your open source community, startup or
                  company
                </strong>
                <Image
                  src={staticURL('/assets/faq/organisation-profiles.png')}
                />
              </li>
              <li>
                <strong>
                  minimalistic stories for sharing your thoughts, challenges and
                  breakthroughs
                </strong>
                <Image src={staticURL('/assets/faq/stories.png')} />
              </li>
              <li>
                <strong>
                  collections to help organise your favorite projects and
                  inspirations on Wikifactory
                </strong>
                <Image src={staticURL('/assets/faq/collections.png')} />
              </li>
              <li>
                <strong>
                  topic pages around areas of interest like #3d-printing,
                  #robotics or #furniture
                </strong>
                <Image src={staticURL('/assets/faq/topics.png')} />
              </li>
              <li>
                <strong>and much more!</strong>
              </li>
            </ul>
          </AccordionStyled>
          <AccordionStyled
            title='Why does Wikifactory exist?'
            unfolded={allExpanded}
          >
            <Paragraph>
              At Wikifactory, we believe that accessible tools and collaborative
              workflows can enable extraordinary things - fast!
            </Paragraph>
            <Paragraph>
              Take the world of software development for example. Thanks to
              accessible tools and collaborative workflows, open source projects
              with contributions coming from all around the world are now
              powering most of the modern web. Similarly, software startups are
              building products and solving problems (big and small) almost
              faster than we can keep track of.
            </Paragraph>
            <Paragraph>
              With the digitisation of manufacturing well underway, Wikifactory
              aims to bring similar tools and workflows to the world of product
              design and production - with the goal of drastically reducing the
              cost, and massively accelerating the speed at which innovative
              products can be brought to market.
            </Paragraph>
            <Paragraph>
              <strong>
                We envision a future in which developing and shipping physical
                products is as collaborative, accessible and low-risk as
                developing and shipping software products is today.
              </strong>
            </Paragraph>
          </AccordionStyled>
          <AccordionStyled
            title='What makes this an alpha?'
            unfolded={allExpanded}
          >
            <Paragraph>
              Right now, Wikifactory is already being used by a growing
              community of designers, engineers, makers, technologists and other
              creative problem solvers to collaboratively develop physical
              products.
            </Paragraph>
            <Paragraph>
              That said, in it&apos;s current stage the platform constitutes the
              core of a wider vision, and is as such considered an
              &quot;alpha&quot;.
            </Paragraph>
            <Paragraph>
              This wider vision is to create an{' '}
              <strong> Internet of Production</strong> (IoP). Simpy put, an IoP
              is a network which makes product and stardarised component data
              accessible to anyone. As machines, sensors and automation tools
              connect to the network, production runs become inreasingly
              distributed, scale-free and on-demand.
            </Paragraph>
            <Paragraph>
              Whilst these design-to-production functionalities develop,
              Wikifactory will transition into &quot;beta&quot;.
            </Paragraph>
            <Paragraph>
              If you you&apos;re a manufacturer (big or small) and wish to offer
              your &nbsp;<strong>Factory As A Service</strong> (FAAS), feel free
              to&nbsp;
              <a href='mailto:team@wikifactory.com' target='_top'>
                get in touch via email
              </a>
              .
            </Paragraph>
          </AccordionStyled>
          <AccordionStyled
            title='How can Wikifactory help my open source project today?'
            unfolded={allExpanded}
          >
            <Paragraph>
              Setting up collaboration infrastructure around your open source
              project for things like sharing files, documentation, or getting
              feedback is a time consuming, costly task. Wikifactory takes the
              hassle out of this by providing a free, elegant and user-friendly
              experience so you can invest your time and resources in what you
              care about most - your product and community.
            </Paragraph>
            <Paragraph>
              If you&apos;re open source project already uses a service or
              combination of services for sharing files, documentation, or
              getting feedback, you might still want to consider switching.
              Wikifactory has been designed from the ground up for the needs of
              the product development world.
            </Paragraph>
            <Paragraph>
              What&apos;s more, with the multi-service approach, valuably
              related information ends up siloed in different services, as well
              as gated behind yet another seperate sign up you&apos;re future
              collaborators and/or community members need to complete.
            </Paragraph>
            <Paragraph>
              Perhaps most importantly though, Wikifactory is home to a diverse
              community of designers, engineers, makers, technologists and other
              creative problem solvers keen to grow their skills and learn new
              things by contributing to open source projects like yours.
            </Paragraph>
            <Paragraph>
              Last but not least, we&apos;re excited about every new open source
              project on Wikifactory and enjoy taking the time to understand
              it&apos;s needs. We actively promote projects on our featured page
              and social media channels to help find contributors and move ideas
              forward, and are always happy to connect creators to relevant
              manufacturers, component providers and distributors in our network
              whilst developing our upcoming API to the physical world.
            </Paragraph>
          </AccordionStyled>
          <AccordionStyled
            title='Are all public projects open source?'
            unfolded={allExpanded}
          >
            <Paragraph>
              Under International Copyright Law, anything that is shared
              publicly is by default copyrighted by its creator, which means
              that only those public projects that have explicitly added an
              (optional) open source license to their work, are open source.
            </Paragraph>
            <Paragraph>
              Whilst we are big fans of open source, we understand that
              it&apos;s not for everyone nor practical for everything, and we
              certainly didn&apos;t want to force it onto anyone.
            </Paragraph>
            <Paragraph>
              Furthermore, &quot;open source&quot; can mean many different
              things - depending on the details of the many different licenses
              that exist. We have linked the more popular licenses to a summary
              page{' '}
              <a
                rel='noopener noreferrer'
                href='https://choosealicense.com/licenses/cc-by-4.0/'
                target='_blank'
              >
                (like this one)
              </a>{' '}
              that clearly states the license&apos;s permissions, conditions and
              limitiations. We recommend you give these a read, so you&apos;re
              always sure where you stand legally when repurposing or building
              on other people&apos;s work.
            </Paragraph>
          </AccordionStyled>
          <AccordionStyled
            title='What improvements can I expect in the coming months (Q1 2019)?'
            unfolded={allExpanded}
          >
            <Paragraph>
              Short term feature development is largely feedback driven and we
              love squeezing in improvements that add real value to our users.
              If you&apos;ve got feature requests or suggestions for
              improvements, please message us via the platforms built in chat
              (bottom right corner).
            </Paragraph>
            <Paragraph>
              By Q1 2019, we hope to add most of the following feature requests
              (in no particular order):
            </Paragraph>
            <ul>
              <li>
                <strong>add a forum to organisations</strong>
              </li>
              <li>
                <strong>explode 3d models</strong> ✅
              </li>
              <li>
                <strong>annotate 3d models</strong>
              </li>
              <li>
                <strong>view assembly trees of 3d models</strong>
              </li>
              <li>
                <strong>mention users in comments, issues etc</strong> ✅
              </li>
              <li>
                <strong>
                  reference files and issues in comments and readmes
                </strong>
              </li>
              <li>
                <strong>
                  preview 3d files in readmes, contributions, issues etc
                </strong>
              </li>
              <li>
                <strong>fork and merge projects</strong>
              </li>
              <li>
                <strong>
                  support drag and drop of folders into the repository
                </strong>
              </li>
              <li>
                <strong>
                  visually resolve conflicts between project versions
                </strong>
              </li>
              <li>
                <strong>
                  publish specific versions of projects as a &quot;release&quot;
                </strong>
              </li>
              <li>
                <strong>
                  reference files and standardised components in a strucuted BOM
                </strong>
              </li>
              <li>
                <strong>
                  import projects from github, gitlab, thingiverse, hackster etc
                </strong>
              </li>
              <li>
                <strong>import discussions from google+</strong>
              </li>
              <li>
                <strong>download all your user data</strong>
              </li>
            </ul>
            <Paragraph>
              For a sense of our long term development goals, check out our
              answer to &quot;What makes this an alpha?&quot; up above.
            </Paragraph>
          </AccordionStyled>
          <AccordionStyled
            title="What is Wikifactory's business model?"
            unfolded={allExpanded}
          >
            <Paragraph>
              Soon™ we will introduce a paid subscription model for private
              projects. The details of this are not set into stone yet, but
              structurally it will likely be similar to{' '}
              <a
                rel='noopener noreferrer'
                target='_blank'
                href='https://github.com/pricing'
              >
                Github&apos;s pricing model
              </a>
              .
            </Paragraph>
            <Paragraph>
              Any private projects you create during the alpha, will be exempt
              from future charges.
            </Paragraph>
          </AccordionStyled>
          <AccordionStyled
            title="I've got a question that hasn't been answered. Now what?"
            unfolded={allExpanded}
          >
            <Paragraph>
              You can ask us anything via the platform&apos;s built-in chat
              (bottom right corner), or{' '}
              <a href='mailto:team@wikifactory.com' target='_top'>
                via email
              </a>
              . We&apos;re always excited about hearing from you, and will get
              back to you as soon as we can!
            </Paragraph>
          </AccordionStyled>
        </Wrapper>
      </div>
    );
  }
}

FAQPage.propTypes = {
  t: PropTypes.func,
};
